import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TableSortLabel,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Snackbar
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  ArrowUpward,
  ArrowDownward,
  Clear,
  Visibility
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: string;
  organization: string;
  username: string;
  lastLogin?: string;
  status: 'active' | 'inactive';
  permissions: string[];
  windowsCredentials?: string;
  authorization?: string;
}

type SortField = 'username' | 'organization' | 'status' | 'lastLogin';
type SortDirection = 'asc' | 'desc';

interface ColumnFilter {
  username: string;
  organization: string;
  status: string;
  lastLogin: string;
}

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Form state for add/edit user
  const [formData, setFormData] = useState({
    username: '',
    organization: '',
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('username');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Column filters state
  const [columnFilters, setColumnFilters] = useState<ColumnFilter>({
    username: '',
    organization: '',
    status: '',
    lastLogin: ''
  });
  
  // Filter menu state
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<keyof ColumnFilter | null>(null);
  
  const { organization, user: currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, [organization]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      if (!organization) return;
      
      const [usersData, userActivitySummary] = await Promise.all([
        authAPI.getUsers(), // Fetch users from all organizations
        authAPI.getUserActivitySummary()
      ]);
      
      // Create user data from users and activity summary
      const userMap = new Map<string, User>();
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      
      usersData.forEach((user: any) => {
        const userActivity = userActivitySummary.find((activity: any) => 
          activity.username === user.username
        );
        
        const isActive = userActivity?.lastActivity && userActivity.lastActivity >= thirtyDaysAgo;
        
        userMap.set(user.username, {
          id: user.username,
          organization: user.organization,
          username: user.username,
          lastLogin: userActivity?.lastActivity ? new Date(userActivity.lastActivity).toISOString() : undefined,
          status: isActive ? 'active' : 'inactive',
          permissions: [], // Would need to fetch from organization
          windowsCredentials: user.windowsCredentials || '',
          authorization: user.authorization || ''
        });
      });
      
      setUsers(Array.from(userMap.values()));
    } catch (err: any) {
      setError('Failed to load users');
      console.error('Users error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Sorting function
  const sortUsers = (users: User[]): User[] => {
    return [...users].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      // Handle date sorting for lastLogin
      if (sortField === 'lastLogin') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }
      
      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Column filtering function
  const applyColumnFilters = (users: User[]): User[] => {
    return users.filter(user => {
      const usernameMatch = !columnFilters.username || 
        user.username.toLowerCase().includes(columnFilters.username.toLowerCase());
      const organizationMatch = !columnFilters.organization || 
        user.organization.toLowerCase().includes(columnFilters.organization.toLowerCase());
      const statusMatch = !columnFilters.status || 
        user.status.toLowerCase().includes(columnFilters.status.toLowerCase());
      const lastLoginMatch = !columnFilters.lastLogin || 
        (user.lastLogin && user.lastLogin.toLowerCase().includes(columnFilters.lastLogin.toLowerCase()));
      
      return usernameMatch && organizationMatch && statusMatch && lastLoginMatch;
    });
  };

  const filteredUsers = applyColumnFilters(sortUsers(users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.organization.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesFilter;
  })));

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditMode(true);
    setFormData({
      username: user.username,
      organization: user.organization,
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setEditMode(false);
    setFormData({
      username: '',
      organization: organization?.name || '',
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }
    
    if (!formData.organization.trim()) {
      errors.organization = 'Organization is required';
    }
    
    if (!editMode) {
      if (!formData.password.trim()) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }
    
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveCustomer = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      
      if (editMode && selectedUser) {
        // Update existing user
        await authAPI.updateUser(selectedUser.organization, selectedUser.username, {
          organization: formData.organization,
          username: formData.username,
          password: formData.password || undefined
        });
      } else {
        // Create new user
        await authAPI.createUser(formData.organization, {
          organization: formData.organization,
          username: formData.username,
          password: formData.password
        });
      }
      
      // Refresh the users list
      await fetchUsers();
      
      // Close dialog and reset form
      setDialogOpen(false);
      setSelectedUser(null);
      setFormData({
        username: '',
        organization: '',
        password: '',
        confirmPassword: ''
      });
      setFormErrors({});
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save customer');
      console.error('Save customer error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      return;
    }
    
    try {
      await authAPI.deleteUser(user.organization, user.username);
      await fetchUsers(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
      console.error('Delete user error:', err);
    }
  };

  // Sorting handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter handlers
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, column: keyof ColumnFilter) => {
    setFilterMenuAnchor(event.currentTarget);
    setActiveFilterColumn(column);
  };

  const handleFilterClose = () => {
    setFilterMenuAnchor(null);
    setActiveFilterColumn(null);
  };

  const handleColumnFilterChange = (column: keyof ColumnFilter, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const clearColumnFilter = (column: keyof ColumnFilter) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: ''
    }));
  };

  const clearAllFilters = () => {
    setColumnFilters({
      username: '',
      organization: '',
      status: '',
      lastLogin: ''
    });
  };

  const handleUserClick = (user: User) => {
    navigate(`/users/${user.organization}/${user.username}/activities`);
  };

  const handleResetAuthorization = async (user: User) => {
    try {
      setSaving(true);
      
      // Check if user is trying to reset their own authorization
      if (currentUser && currentUser.username === user.username && currentUser.organization === user.organization) {
        setSnackbarMessage('Cannot reset your own authorization. This will log you out.');
        setSnackbarOpen(true);
        return;
      }
      
      // Reset authorization by updating user with null authorization
      await authAPI.updateUser(user.organization, user.username, {
        organization: user.organization,
        username: user.username,
        password: '', // Empty to trigger authorization-only update
        windowsCredentials: '', // Empty to trigger authorization-only update
        authorization: null // Reset authorization to null
      });
      setSnackbarMessage('Authorization reset successfully');
      setSnackbarOpen(true);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      setSnackbarMessage('Failed to reset authorization');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleResetWindowsCredentials = async (user: User) => {
    try {
      setSaving(true);
      // Reset Windows credentials by updating with empty string
      await authAPI.updateUserCredentials(user.organization, user.username, { windowsCredentials: '' });
      setSnackbarMessage('Windows credentials reset successfully');
      setSnackbarOpen(true);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      setSnackbarMessage('Failed to reset Windows credentials');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  // Get unique values for dropdown filters
  const getUniqueValues = (field: keyof User): string[] => {
    return Array.from(new Set(users.map(user => String(user[field])).filter(Boolean))) as string[];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddUser}
        >
          Add User
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant={filterStatus === 'all' ? 'contained' : 'outlined'}
              onClick={() => setFilterStatus('all')}
              fullWidth
            >
              All
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant={filterStatus === 'active' ? 'contained' : 'outlined'}
              onClick={() => setFilterStatus('active')}
              fullWidth
            >
              Active
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={clearAllFilters}
              startIcon={<Clear />}
              fullWidth
              disabled={Object.values(columnFilters).every(filter => !filter)}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Customer Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h4">
                {users.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Users
              </Typography>
              <Typography variant="h4">
                {users.filter(u => u.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Organizations
              </Typography>
              <Typography variant="h4">
                {new Set(users.map(u => u.organization)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Customer Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <TableSortLabel
                    active={sortField === 'username'}
                    direction={sortField === 'username' ? sortDirection : 'asc'}
                    onClick={() => handleSort('username')}
                  >
                    Username
                  </TableSortLabel>
                  <Box display="flex" alignItems="center">
                    {columnFilters.username && (
                      <Chip
                        size="small"
                        label={columnFilters.username}
                        onDelete={() => clearColumnFilter('username')}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'username')}
                      color={columnFilters.username ? 'primary' : 'default'}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <TableSortLabel
                    active={sortField === 'organization'}
                    direction={sortField === 'organization' ? sortDirection : 'asc'}
                    onClick={() => handleSort('organization')}
                  >
                    Organization
                  </TableSortLabel>
                  <Box display="flex" alignItems="center">
                    {columnFilters.organization && (
                      <Chip
                        size="small"
                        label={columnFilters.organization}
                        onDelete={() => clearColumnFilter('organization')}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'organization')}
                      color={columnFilters.organization ? 'primary' : 'default'}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? sortDirection : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                  <Box display="flex" alignItems="center">
                    {columnFilters.status && (
                      <Chip
                        size="small"
                        label={columnFilters.status}
                        onDelete={() => clearColumnFilter('status')}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'status')}
                      color={columnFilters.status ? 'primary' : 'default'}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <TableSortLabel
                    active={sortField === 'lastLogin'}
                    direction={sortField === 'lastLogin' ? sortDirection : 'asc'}
                    onClick={() => handleSort('lastLogin')}
                  >
                    Last Login
                  </TableSortLabel>
                  <Box display="flex" alignItems="center">
                    {columnFilters.lastLogin && (
                      <Chip
                        size="small"
                        label={columnFilters.lastLogin}
                        onDelete={() => clearColumnFilter('lastLogin')}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleFilterClick(e, 'lastLogin')}
                      color={columnFilters.lastLogin ? 'primary' : 'default'}
                    >
                      <FilterList fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2">Windows Credentials</Typography>
                </Box>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Button
                    variant="text"
                    onClick={() => handleUserClick(user)}
                    sx={{ 
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      p: 0,
                      minWidth: 'auto',
                      textAlign: 'left'
                    }}
                  >
                    {user.username}
                  </Button>
                </TableCell>
                <TableCell>{user.organization}</TableCell>
                <TableCell>
                  <Chip
                    label={user.status}
                    color={user.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.lastLogin 
                    ? new Date(user.lastLogin).toLocaleDateString()
                    : 'Never'
                  }
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ 
                    maxWidth: 200, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {user.windowsCredentials || 'Not set'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleUserClick(user)}
                      title="View User Activities"
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditUser(user)}
                      title="Edit User"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleResetAuthorization(user)}
                      title={currentUser && currentUser.username === user.username && currentUser.organization === user.organization 
                        ? "Cannot reset your own authorization" 
                        : "Reset Authorization"}
                      color="warning"
                      disabled={Boolean(currentUser && currentUser.username === user.username && currentUser.organization === user.organization)}
                    >
                      <Clear />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleResetWindowsCredentials(user)}
                      title="Reset Windows Credentials"
                      color="info"
                    >
                      <Clear />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteUser(user)}
                      title="Delete User"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Add Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Customer' : 'Add Customer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => handleFormChange('username', e.target.value)}
                error={!!formErrors.username}
                helperText={formErrors.username}
                disabled={editMode}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organization"
                value={formData.organization}
                onChange={(e) => handleFormChange('organization', e.target.value)}
                error={!!formErrors.organization}
                helperText={formErrors.organization}
                disabled={editMode}
                required
              />
            </Grid>
            {!editMode && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleFormChange('confirmPassword', e.target.value)}
                    error={!!formErrors.confirmPassword}
                    helperText={formErrors.confirmPassword}
                    required
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCustomer} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : (editMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={handleFilterClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '250px',
          },
        }}
      >
        {activeFilterColumn && (
          <>
            <MenuItem disabled>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Filter by {activeFilterColumn}
              </Typography>
            </MenuItem>
            <MenuItem>
              <TextField
                fullWidth
                size="small"
                placeholder={`Filter ${activeFilterColumn}...`}
                value={columnFilters[activeFilterColumn]}
                onChange={(e) => handleColumnFilterChange(activeFilterColumn, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </MenuItem>
            {activeFilterColumn === 'status' && (
              <MenuItem>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={columnFilters.status}
                    onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                    input={<OutlinedInput label="Status" />}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </MenuItem>
            )}
            {activeFilterColumn === 'organization' && (
              <MenuItem>
                <FormControl fullWidth size="small">
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={columnFilters.organization}
                    onChange={(e) => handleColumnFilterChange('organization', e.target.value)}
                    input={<OutlinedInput label="Organization" />}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {getUniqueValues('organization').map((org, index) => (
                      <MenuItem key={`org-${index}`} value={org}>
                        {org}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MenuItem>
            )}
            {columnFilters[activeFilterColumn] && (
              <MenuItem onClick={() => clearColumnFilter(activeFilterColumn)}>
                <Box display="flex" alignItems="center">
                  <Clear sx={{ mr: 1 }} />
                  Clear Filter
                </Box>
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default Users;
